import { action, makeObservable, observable } from 'mobx';

import { getUserLanguage } from '../../i18n/helpers';
import { getRequest, postRequest } from '../../services/api';
import { resolveFlag } from '../../services/launchDarkly';
import { devError } from '../../utils/log';
import { singleFlight } from '../../utils/singleFlight';
import type { FeatureFetchStatus, FeatureManagerDialogType, IFeatureManagerStore } from '../feature-manager/types';
import { type RootStore } from '../RootStore';
import { Loadable } from '../traits/Loadable';
import { EnumOpenDialog } from '../ui/types';

import { type TermsOfServiceDTO, type TermsOfService } from './TermsOfServiceStore.types';

export class TermsOfServiceStore extends Loadable implements IFeatureManagerStore {
  private root: RootStore;

  constructor(_rootStore: RootStore) {
    super();
    this.root = _rootStore;
    makeObservable(this);
  }

  @observable
  public termsOfService: TermsOfService | null | undefined;

  public dialogTypes: FeatureManagerDialogType[] = ['TermsOfService'];

  @observable
  private fetchingTermsOfService = false;

  @observable
  public isTermsOfServiceDialogVisible = false;

  public startDialogs() {
    if (this.termsOfService) {
      this.setTermsOfServiceDialogVisible(true);
      return true;
    }
    return false;
  }

  public fetch = singleFlight(async (): Promise<FeatureFetchStatus> => {
    const featureFlag = resolveFlag('terms_of_service_update', true);
    if (!featureFlag) return 'OFF';

    try {
      this.setFetchingTermsOfService(true);

      const termsOfService = await this.fetchTermOfService();

      if (termsOfService) {
        this.setTermsOfService(termsOfService);
        return 'READY';
      }
      return 'OFF';
    } catch (error) {
      devError('TermsOfServiceStore.fetch', error);
      return 'ERROR';
    } finally {
      this.setFetchingTermsOfService(false);
    }
  });

  @action
  public setTermsOfService(value: TermsOfService | null) {
    this.termsOfService = value;
  }

  @action
  public setFetchingTermsOfService(value: boolean) {
    this.fetchingTermsOfService = value;
  }

  @action
  public setTermsOfServiceDialogVisible(value: boolean) {
    if (value) {
      this.root.uiStore.setOpenDialog({
        dialog: EnumOpenDialog.isOpenTermsOfService,
      });
    } else {
      this.root.uiStore.setCloseDialog();
    }
    this.isTermsOfServiceDialogVisible = value;
  }

  public async fetchTermOfService() {
    const userLanguage = getUserLanguage(this.root.userStore.profile?.language);
    const {
      data: { termsOfService },
    } = await getRequest<{ data: TermsOfServiceDTO }>('terms-of-service', { language: userLanguage });

    return termsOfService;
  }

  public async acceptTermsOfService() {
    if (!this.termsOfService) {
      return;
    }
    try {
      await postRequest('terms-of-service', { tosId: this.termsOfService.id });
      this.setTermsOfService(null);
    } catch (error) {
      devError('TermsOfServiceStore.fetch', error);
    }
  }
}
